import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth, defaultRoute } from "@/context/AuthContext";
import { LanguageProvider } from "@/lib/i18n";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import GatePage from "@/pages/GatePage";
import Login from "@/pages/Login";
import MyDaily from "@/pages/MyDaily";
import UpdateForm from "@/pages/UpdateForm";
import MyHistory from "@/pages/MyHistory";
import ActionCenter from "@/pages/ActionCenter";
import Projects from "@/pages/Projects";
import ProjectDashboard from "@/pages/ProjectDashboard";
import Warnings from "@/pages/Warnings";
import Portfolio from "@/pages/Portfolio";
import TeamWorkload from "@/pages/TeamWorkload";
import Admin from "@/pages/Admin";
import Reports from "@/pages/Reports";
import BlockerCenter from "@/pages/BlockerCenter";
import Board from "@/pages/Board";
import Backlog from "@/pages/Backlog";
import WorkItems from "@/pages/WorkItems";
import WorkItemPage from "@/pages/WorkItemPage";
import WorkflowEditor from "@/pages/WorkflowEditor";
import SprintReport from "@/pages/SprintReport";
import MyWork from "@/pages/MyWork";
import WorkQuery from "@/pages/WorkQuery";
import { SetPassword, ForcePasswordChange } from "@/pages/PasswordPages";

function GateGuard({ children }) {
  const [ready, setReady] = useState(
    () => !!localStorage.getItem("apha_gate") || window.location.pathname === "/gate"
  );
  useEffect(() => {
    if (ready) return;
    api.get("/gate/status")
      .then((r) => {
        if (r.data.enabled) window.location.assign("/gate");
        else setReady(true);
      })
      .catch(() => setReady(true));
  }, [ready]);
  if (!ready)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading...</div>;
  return children;
}

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (user === undefined)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={defaultRoute(user.role)} replace />;
  return <Layout>{children}</Layout>;
}

function PasswordGate() {
  const { user } = useAuth();
  if (user === undefined)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.must_change_password) return <Navigate to={defaultRoute(user.role)} replace />;
  return <ForcePasswordChange />;
}

function HomeRedirect() {
  const { user } = useAuth();
  if (user === undefined)
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground text-sm">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  return <Navigate to={defaultRoute(user.role)} replace />;
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <BrowserRouter>
          <GateGuard>
          <Routes>
            <Route path="/gate" element={<GatePage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/change-password" element={<PasswordGate />} />
            <Route path="/" element={<HomeRedirect />} />
            <Route path="/my-daily" element={<Protected><MyDaily /></Protected>} />
            <Route path="/update/:projectId" element={<Protected><UpdateForm /></Protected>} />
            <Route path="/my-history" element={<Protected><MyHistory /></Protected>} />
            <Route path="/action-center" element={<Protected><ActionCenter /></Protected>} />
            <Route path="/projects" element={<Protected><Projects /></Protected>} />
            <Route path="/projects/:projectId" element={<Protected><ProjectDashboard /></Protected>} />
            <Route path="/projects/:projectId/board" element={<Protected><Board /></Protected>} />
            <Route path="/projects/:projectId/backlog" element={<Protected><Backlog /></Protected>} />
            <Route path="/projects/:projectId/work" element={<Protected><WorkItems /></Protected>} />
            <Route path="/projects/:projectId/workflow" element={<Protected><WorkflowEditor /></Protected>} />
            <Route path="/work/:key" element={<Protected><WorkItemPage /></Protected>} />
            <Route path="/sprints/:sprintId" element={<Protected><SprintReport /></Protected>} />
            <Route path="/my-work" element={<Protected><MyWork /></Protected>} />
            <Route path="/query" element={<Protected><WorkQuery /></Protected>} />
            <Route path="/warnings" element={<Protected><Warnings /></Protected>} />
            <Route path="/blockers" element={<Protected><BlockerCenter /></Protected>} />
            <Route path="/portfolio" element={<Protected><Portfolio /></Protected>} />
            <Route path="/team-workload" element={<Protected roles={["PM", "LEAD", "EXECUTIVE", "ADMIN", "AUDITOR"]}><TeamWorkload /></Protected>} />
            <Route path="/reports" element={<Protected><Reports /></Protected>} />
            <Route path="/admin" element={<Protected><Admin /></Protected>} />
            <Route path="*" element={<HomeRedirect />} />
          </Routes>
          </GateGuard>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
